import { useState, useCallback, useRef } from "react";
import {
  AnnotationDataFormat,
  AnnotationSelectEventArgs,
  CircleSettings,
  CustomStampModel,
  CustomStampSettings,
  FreeTextSettings,
  IPoint,
  PdfAnnotationBase,
  PdfAnnotationBaseModel,
  PdfViewerComponent,
  RectangleSettings,
} from "@syncfusion/ej2-react-pdfviewer";
import { ImageElement } from "@syncfusion/ej2-drawings";
import { v4 as uuidv4 } from "uuid";
import { get } from "lodash";
import { ExpressionData, ExpressionTypes, AnnotationDefaults } from "../types/types";

export const expressiveTypes: ExpressionTypes[] = [
  ExpressionTypes.FreeText,
  ExpressionTypes.TextExpression,
  ExpressionTypes.ImageExpression,
  ExpressionTypes.CheckboxExpression,
];

export const freeTextAnnotationDimensionsStyle: Pick<
  FreeTextSettings | ExpressionDimensions,
  "width" | "height"
> = {
  width: 150,
  height: 24,
};

export const shapeAnnotationDimensionsStyle: Pick<
  RectangleSettings | CircleSettings | ExpressionDimensions,
  "width" | "height"
> = {
  width: 100,
  height: 100,
};

export const imageAnnotationDimensionsStyle: Pick<
  CustomStampSettings | ExpressionDimensions,
  "width" | "height"
> = {
  width: 250,
  height: 50,
};

export const checkboxAnnotationDimensionsStyle: Pick<
  RectangleSettings | ExpressionDimensions,
  "width" | "height"
> = {
  width: 15,
  height: 15,
};

export interface ExpressionDimensions {
  width: number;
  height: number;
}

export interface UsePdfTemplateExpressionReturn {
  // State
  // pdfViewerRef.current: PdfViewerComponent | null;
  originalImportedData:
    | {
        Rect: any;
        AnnotName: string;
        Apperarance: { [key: string]: string }[];
      }[]
    | undefined;
  pdfViewerId: string | undefined;
  previewData: any;

  // Methods
  handleAnnotationSelect: (args: AnnotationSelectEventArgs) => void;
  updateExpressionSelectedClassName: (
    expressionType: ExpressionTypes,
    isAdd?: boolean
  ) => void;
  removeAllExpressionSelectedClassName: () => void;
  getAnnotationByAnnotationId: (annotationId: string) => any;
  getAnnotationObjectByAnnotationId: (annotationId: string) => any;
  handleAnnotationUnselect: (args: AnnotationSelectEventArgs) => void;
  unselectAnnotation: (annotationId: string) => void;
  unselectAllAnnotations: () => void;
  annotationRemove: () => void;
  addExpressionAnnotation: (
    templateExpression: ExpressionData,
    point: IPoint
  ) => PdfAnnotationBase | undefined;
  getDefaultExpressionAnnotationDimensions: (
    expressionType: ExpressionTypes
  ) => ExpressionDimensions;
  addFreeTextAnnotation: (
    templateExpression: ExpressionData,
    point: IPoint
  ) => PdfAnnotationBase | undefined;
  addRectangleAnnotation: (
    templateExpression: ExpressionData,
    point: IPoint
  ) => PdfAnnotationBase | undefined;
  addCircleAnnotation: (
    templateExpression: ExpressionData,
    point: IPoint
  ) => PdfAnnotationBase | undefined;
  addCheckboxExpressionAnnotation: (
    templateExpression: ExpressionData,
    point: IPoint
  ) => PdfAnnotationBase | undefined;
  addTextExpressionAnnotation: (
    templateExpression: ExpressionData,
    point: IPoint
  ) => PdfAnnotationBase | undefined;
  addImageExpressionAnnotation: (
    templateExpression: ExpressionData,
    point: IPoint
  ) => PdfAnnotationBase | undefined;
  getImageExpressionPlaceholderImage: (
    width: number,
    height: number,
    placeholderText: string
  ) => string;
  designAnnotations: () => void;
  previewAnnotations: (
    data?: any,
    getDocument?: (value: string) => Promise<any>,
    getDocumentBlobUrl?: (docBlobPart: any) => Promise<string>
  ) => Promise<void>;
  updateCustomStampAnnotationImage: (
    annotationId: string,
    imageSource: string
  ) => void;
  updateAnnotationProperties: (
    annotationId: string,
    annotationUpdateSettings: PdfAnnotationBaseModel
  ) => void;
  goToPage: (pageNumber: number) => void;
  getExpressionAnnotationByExpressionId: (
    expressionId: string | undefined
  ) => PdfAnnotationBase | undefined;
  exportAnnotations: () => Promise<string | undefined>;
  importAnnotations: (exportedAnnotationsXFDFBase64: string) => Promise<void>;
  selectAnnotation: (annotationId: string) => void;
  setup: (args: { pdfViewerId: string }) => void;
  updateOriginalImportedData: (originalImportedData?: any) => void;
}

export const usePdfTemplateExpression = (
  pdfViewerRef: React.RefObject<PdfViewerComponent | null>,
  onPreviewAnnotations?: (data?: any, annotationsData?: string) => void,
  annotationDefaults?: AnnotationDefaults
): UsePdfTemplateExpressionReturn => {
  // State
  const [originalImportedData, setOriginalImportedData] = useState<
    | {
        Rect: any;
        AnnotName: string;
        Apperarance: { [key: string]: string }[];
      }[]
    | undefined
  >(undefined);
  const [pdfViewerId, setPdfViewerId] = useState<string | undefined>(undefined);
  const [previewData, setPreviewData] = useState<any>({});

  // Refs for styles (these don't need to trigger re-renders)
  const annotationFreeTextExpressionStyle = useRef<
    Pick<FreeTextSettings, "fillColor" | "borderColor" | "borderWidth">
  >({
    fillColor: "#fdf8d8",
    borderColor: "#f1a01a",
    borderWidth: 2,
  });

  const annotationImageExpressionStyle = useRef<
    Pick<CanvasRenderingContext2D, "fillStyle" | "strokeStyle" | "lineWidth">
  >({
    fillStyle: "#fdf8d8",
    strokeStyle: "#f1a01a",
    lineWidth: 4,
  });

  const annotationFreeTexStyle = useRef<
    Pick<FreeTextSettings, "fillColor" | "borderColor" | "borderWidth">
  >({
    fillColor: "#d8f0fd",
    borderColor: "#1a84f1",
    borderWidth: 2,
  });

  const annotationShapeStyle = useRef<
    Pick<
      RectangleSettings | CircleSettings,
      "fillColor" | "strokeColor" | "thickness"
    >
  >({
    fillColor: "#000",
    strokeColor: "#000",
    thickness: 2,
  });

  // Helper functions
  const isGuid = useCallback((value: string): boolean => {
    const guidPattern =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return guidPattern.test(value);
  }, []);

  const getBaseImageAnnotationData = useCallback(
    async (
      value: string,
      getDocument?: (value: string) => Promise<any>,
      getDocumentBlobUrl?: (docBlobPart: any) => Promise<string>
    ): Promise<string> => {
      if (!value || !getDocument || !getDocumentBlobUrl) {
        return "";
      }

      if (isGuid(value)) {
        const docBlobPart = await getDocument(value);
        if (!docBlobPart) {
          return "";
        }
        return await getDocumentBlobUrl(docBlobPart);
      }

      return value;
    },
    [isGuid]
  );

  // Event handlers
  const handleAnnotationSelect = useCallback(
    (args: AnnotationSelectEventArgs) => {
      if (!pdfViewerRef.current) {
        return;
      }

      const annotation = getAnnotationByAnnotationId(args.annotationId);
      if (annotation?.customData) {
        updateExpressionSelectedClassName(
          (annotation.customData as ExpressionData).expressionType,
          true
        );

        pdfViewerRef.current.toolbar.showAnnotationToolbar(false);
      }
    },
    [pdfViewerRef.current]
  );

  const updateExpressionSelectedClassName = useCallback(
    (expressionType: ExpressionTypes, isAdd?: boolean) => {
      const pdfViewerContainer = document.getElementById(pdfViewerId!);

      // Remove all to cleanup
      removeAllExpressionSelectedClassName();

      if (isAdd === true) {
        pdfViewerContainer?.classList.add(
          `expression-selected-${ExpressionTypes[expressionType].toLowerCase()}`
        );
      }
    },
    [pdfViewerId]
  );

  const removeAllExpressionSelectedClassName = useCallback(() => {
    const pdfViewerContainer = document.getElementById(pdfViewerId!);

    // Remove all to cleanup
    pdfViewerContainer?.classList.remove(`expression-selected-freetext`);
    pdfViewerContainer?.classList.remove(`expression-selected-rectangle`);
    pdfViewerContainer?.classList.remove(`expression-selected-circle`);
    pdfViewerContainer?.classList.remove(`expression-selected-textexpression`);
    pdfViewerContainer?.classList.remove(`expression-selected-imageexpression`);
    pdfViewerContainer?.classList.remove(
      `expression-selected-checkboxexpression`
    );
  }, [pdfViewerId]);

  const getAnnotationByAnnotationId = useCallback(
    (annotationId: string) => {
      if (!pdfViewerRef.current) {
        return;
      }

      return pdfViewerRef.current.annotationCollection.find(
        (x) => x.annotationId === annotationId
      );
    },
    [pdfViewerRef.current]
  );

  const getAnnotationObjectByAnnotationId = useCallback(
    (annotationId: string) => {
      if (!pdfViewerRef.current) {
        return;
      }

      return pdfViewerRef.current.annotations.find(
        (x) => x.annotName === annotationId
      );
    },
    [pdfViewerRef.current]
  );

  const handleAnnotationUnselect = useCallback(
    (args: AnnotationSelectEventArgs) => {
      if (!pdfViewerRef.current) {
        return;
      }

      unselectAnnotation(args.annotationId);
    },
    [pdfViewerRef.current]
  );

  const unselectAnnotation = useCallback(
    (annotationId: string) => {
      if (!pdfViewerRef.current) {
        return;
      }

      const annotation = getAnnotationByAnnotationId(annotationId);

      if (annotation?.customData) {
        updateExpressionSelectedClassName(
          (annotation.customData as ExpressionData).expressionType,
          false
        );

        // Hide
        pdfViewerRef.current.toolbar.showAnnotationToolbar(false);
      }
    },
    [
      pdfViewerRef.current,
      getAnnotationByAnnotationId,
      updateExpressionSelectedClassName,
    ]
  );

  const unselectAllAnnotations = useCallback(() => {
    if (!pdfViewerRef.current) {
      return;
    }

    pdfViewerRef.current?.selectedItems?.annotations?.forEach((annot) => {
      if (annot.annotName) {
        unselectAnnotation(annot.annotName);
      }
    });

    removeAllExpressionSelectedClassName();
  }, [
    pdfViewerRef.current,
    unselectAnnotation,
    removeAllExpressionSelectedClassName,
  ]);

  const annotationRemove = useCallback(() => {
    if (!pdfViewerRef.current) {
      return;
    }

    pdfViewerRef.current?.annotationModule.clearSelection();
    unselectAllAnnotations();
  }, [pdfViewerRef.current, unselectAllAnnotations]);

  const getDefaultExpressionAnnotationDimensions = useCallback(
    (expressionType: ExpressionTypes): ExpressionDimensions => {
      switch (expressionType) {
        case ExpressionTypes.FreeText:
          return freeTextAnnotationDimensionsStyle as ExpressionDimensions;
        case ExpressionTypes.Rectangle:
          return shapeAnnotationDimensionsStyle as ExpressionDimensions;
        case ExpressionTypes.Circle:
          return shapeAnnotationDimensionsStyle as ExpressionDimensions;
        case ExpressionTypes.TextExpression:
          return freeTextAnnotationDimensionsStyle as ExpressionDimensions;
        case ExpressionTypes.ImageExpression:
          return imageAnnotationDimensionsStyle as ExpressionDimensions;
        case ExpressionTypes.CheckboxExpression:
          return checkboxAnnotationDimensionsStyle as ExpressionDimensions;
        default:
          return { width: 0, height: 0 } as ExpressionDimensions;
      }
    },
    []
  );

  const addExpressionAnnotation = useCallback(
    (
      templateExpression: ExpressionData,
      point: IPoint
    ): PdfAnnotationBase | undefined => {
      if (!pdfViewerRef.current) {
        return undefined;
      }

      switch (templateExpression.expressionType) {
        case ExpressionTypes.FreeText:
          return addFreeTextAnnotation(templateExpression, point);
        case ExpressionTypes.Rectangle:
          return addRectangleAnnotation(templateExpression, point);
        case ExpressionTypes.Circle:
          return addCircleAnnotation(templateExpression, point);
        case ExpressionTypes.TextExpression:
          return addTextExpressionAnnotation(templateExpression, point);
        case ExpressionTypes.ImageExpression:
          point = {
            x: point.x,
            y: point.y,
          } as IPoint;
          return addImageExpressionAnnotation(templateExpression, point);
        case ExpressionTypes.CheckboxExpression:
          return addCheckboxExpressionAnnotation(templateExpression, point);
        default:
          return undefined;
      }
    },
    [pdfViewerRef.current]
  );

  const addFreeTextAnnotation = useCallback(
    (
      templateExpression: ExpressionData,
      point: IPoint
    ): PdfAnnotationBase | undefined => {
      if (!pdfViewerRef.current) {
        return undefined;
      }

      // Setup
      templateExpression.id = uuidv4();
      const dimensions =
        templateExpression.expressionDimension ??
        getDefaultExpressionAnnotationDimensions(
          templateExpression.expressionType
        );
      const freeTextSettings = {
        customData: templateExpression,
        offset: point,
        defaultText: templateExpression.expressionPlaceholder,
        pageNumber: pdfViewerRef.current.currentPageNumber,
        isReadonly: false,
        ...annotationFreeTexStyle.current,
        ...dimensions,
      } as FreeTextSettings;

      // Apply defaults if provided
      if (annotationDefaults) {
        if (annotationDefaults.fontSize !== undefined) {
          freeTextSettings.fontSize = annotationDefaults.fontSize;
        }
        if (annotationDefaults.fontFamily !== undefined) {
          freeTextSettings.fontFamily = annotationDefaults.fontFamily;
        }
        if (annotationDefaults.fontColor !== undefined) {
          freeTextSettings.fontColor = annotationDefaults.fontColor;
        }
      }

      // Add
      pdfViewerRef.current.annotationModule.addAnnotation(
        "FreeText",
        freeTextSettings
      );

      return getExpressionAnnotationByExpressionId(templateExpression.id);
    },
    [pdfViewerRef.current, getDefaultExpressionAnnotationDimensions, annotationDefaults]
  );

  const addRectangleAnnotation = useCallback(
    (
      templateExpression: ExpressionData,
      point: IPoint
    ): PdfAnnotationBase | undefined => {
      if (!pdfViewerRef.current) {
        return undefined;
      }

      // Setup
      templateExpression.id = uuidv4();
      const dimensions =
        templateExpression.expressionDimension ??
        getDefaultExpressionAnnotationDimensions(
          templateExpression.expressionType
        );
      const rectangleSettings = {
        customData: templateExpression,
        offset: point,
        pageNumber: pdfViewerRef.current.currentPageNumber,
        ...annotationShapeStyle.current,
        ...dimensions,
      } as RectangleSettings;

      // Add
      pdfViewerRef.current.annotationModule.addAnnotation(
        "Rectangle",
        rectangleSettings
      );

      return getExpressionAnnotationByExpressionId(templateExpression.id);
    },
    [pdfViewerRef.current, getDefaultExpressionAnnotationDimensions]
  );

  const addCircleAnnotation = useCallback(
    (
      templateExpression: ExpressionData,
      point: IPoint
    ): PdfAnnotationBase | undefined => {
      if (!pdfViewerRef.current) {
        return undefined;
      }

      // Setup
      templateExpression.id = uuidv4();
      const dimensions =
        templateExpression.expressionDimension ??
        getDefaultExpressionAnnotationDimensions(
          templateExpression.expressionType
        );
      const rectangleSettings = {
        customData: templateExpression,
        offset: point,
        pageNumber: pdfViewerRef.current.currentPageNumber,
        ...annotationShapeStyle.current,
        ...dimensions,
      } as CircleSettings;

      // Add
      pdfViewerRef.current.annotationModule.addAnnotation(
        "Circle",
        rectangleSettings
      );

      return getExpressionAnnotationByExpressionId(templateExpression.id);
    },
    [pdfViewerRef.current, getDefaultExpressionAnnotationDimensions]
  );

  const addCheckboxExpressionAnnotation = useCallback(
    (
      templateExpression: ExpressionData,
      point: IPoint
    ): PdfAnnotationBase | undefined => {
      if (!pdfViewerRef.current) {
        return undefined;
      }

      // Setup
      templateExpression.id = uuidv4();
      const dimensions =
        templateExpression.expressionDimension ??
        getDefaultExpressionAnnotationDimensions(
          templateExpression.expressionType
        );
      const rectangleSettings = {
        customData: templateExpression,
        offset: point,
        pageNumber: pdfViewerRef.current.currentPageNumber,
        ...annotationShapeStyle.current,
        ...dimensions,
      } as RectangleSettings;

      // Add
      pdfViewerRef.current.annotationModule.addAnnotation(
        "Rectangle",
        rectangleSettings
      );

      return getExpressionAnnotationByExpressionId(templateExpression.id);
    },
    [pdfViewerRef.current, getDefaultExpressionAnnotationDimensions]
  );

  const addTextExpressionAnnotation = useCallback(
    (
      templateExpression: ExpressionData,
      point: IPoint
    ): PdfAnnotationBase | undefined => {
      if (!pdfViewerRef.current) {
        return undefined;
      }

      // Setup
      templateExpression.id = uuidv4();
      const dimensions =
        templateExpression.expressionDimension ??
        getDefaultExpressionAnnotationDimensions(
          templateExpression.expressionType
        );
      const freeTextSettings = {
        customData: templateExpression,
        offset: point,
        defaultText: templateExpression.expressionPlaceholder,
        pageNumber: pdfViewerRef.current.currentPageNumber,
        isReadonly: true,
        ...annotationFreeTextExpressionStyle.current,
        ...dimensions,
      } as FreeTextSettings;

      // Apply defaults if provided
      if (annotationDefaults) {
        if (annotationDefaults.fontSize !== undefined) {
          freeTextSettings.fontSize = annotationDefaults.fontSize;
        }
        if (annotationDefaults.fontFamily !== undefined) {
          freeTextSettings.fontFamily = annotationDefaults.fontFamily;
        }
        if (annotationDefaults.fontColor !== undefined) {
          freeTextSettings.fontColor = annotationDefaults.fontColor;
        }
      }

      // Add
      pdfViewerRef.current.annotationModule.addAnnotation(
        "FreeText",
        freeTextSettings
      );

      return getExpressionAnnotationByExpressionId(templateExpression.id);
    },
    [pdfViewerRef.current, getDefaultExpressionAnnotationDimensions, annotationDefaults]
  );

  const getImageExpressionPlaceholderImage = useCallback(
    (width: number, height: number, placeholderText: string): string => {
      const w = Math.round(width);
      const h = Math.round(height);

      // x Resolution
      const ratio = 2;

      const canvas = document.createElement("canvas");
      canvas.width = w * ratio;
      canvas.height = h * ratio;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";

      // Context
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return "";
      }

      // Scale (Resolution)
      ctx.scale(ratio, ratio);

      // Fill
      ctx.fillStyle = annotationImageExpressionStyle.current.fillStyle;
      ctx.fillRect(0, 0, w, h);

      // Stroke
      ctx.strokeStyle = annotationImageExpressionStyle.current.strokeStyle;
      ctx.lineWidth = annotationImageExpressionStyle.current.lineWidth;
      ctx.strokeRect(0, 0, w, h);

      // Text
      ctx.font = "13px Roboto";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#000";
      ctx.fillText(placeholderText, w / 2, h / 2);

      const dataURL = canvas.toDataURL("image/png");

      return dataURL;
    },
    []
  );

  const addImageExpressionAnnotation = useCallback(
    (
      templateExpression: ExpressionData,
      point: IPoint
    ): PdfAnnotationBase | undefined => {
      if (!pdfViewerRef.current) {
        return undefined;
      }

      // Setup
      templateExpression.id = uuidv4();
      const dimensions =
        templateExpression.expressionDimension ??
        getDefaultExpressionAnnotationDimensions(
          templateExpression.expressionType
        );

      const customStampSettings = {
        customData: templateExpression,
        customStamps: [
          {
            customStampName: templateExpression.expressionName,
            customStampImageSource: getImageExpressionPlaceholderImage(
              dimensions.width,
              dimensions.height,
              templateExpression.expressionPlaceholder ?? ""
            ),
          } as CustomStampModel,
        ],
        offset: point,
        pageNumber: pdfViewerRef.current.currentPageNumber,
        isReadonly: true,
        ...dimensions,
      } as unknown as CustomStampSettings;

      // Add
      pdfViewerRef.current.annotationModule.addAnnotation(
        "Stamp",
        customStampSettings
      );

      return getExpressionAnnotationByExpressionId(templateExpression.id);
    },
    [
      pdfViewerRef.current,
      getDefaultExpressionAnnotationDimensions,
      getImageExpressionPlaceholderImage,
    ]
  );

  const designAnnotations = useCallback(() => {
    if (!pdfViewerRef.current) {
      return;
    }

    // Unselect
    unselectAllAnnotations();

    pdfViewerRef.current.annotationCollection.forEach((annotation: any) => {
      const annotCustomData = annotation.customData as ExpressionData;
      if (
        annotation.customData &&
        expressiveTypes.includes(annotCustomData?.expressionType)
      ) {
        switch (annotation.shapeAnnotationType) {
          case "FreeText":
            {
              if (
                annotCustomData.expressionType ===
                ExpressionTypes.TextExpression
              ) {
                updateAnnotationProperties(annotation.annotationId, {
                  dynamicText: annotCustomData.expressionPlaceholder,
                  fillColor:
                    annotationFreeTextExpressionStyle.current.fillColor,
                  strokeColor:
                    annotationFreeTextExpressionStyle.current.borderColor,
                  thickness:
                    annotationFreeTextExpressionStyle.current.borderWidth,
                });
              } else {
                // FreeText
                updateAnnotationProperties(annotation.annotationId, {
                  dynamicText: annotation.dynamicText,
                  fillColor: annotationFreeTexStyle.current.fillColor,
                  strokeColor: annotationFreeTexStyle.current.borderColor,
                  thickness: annotationFreeTexStyle.current.borderWidth,
                });
              }
            }
            break;
          case "Square":
            {
              if (
                annotCustomData.expressionType ===
                ExpressionTypes.CheckboxExpression
              ) {
                updateAnnotationProperties(annotation.annotationId, {
                  fillColor: annotationShapeStyle.current.fillColor,
                  strokeColor: annotationShapeStyle.current.strokeColor,
                  thickness: annotationShapeStyle.current.thickness,
                });
              }
            }
            break;
          case "stamp":
            {
              let bounds = annotation.bounds;
              if (!bounds) {
                // It has not being loaded yet. Fetch from initial import.
                const ogImportedAnnotation = originalImportedData?.find(
                  (x) => x.AnnotName == annotation.annotationId
                );

                if (ogImportedAnnotation) {
                  bounds = {
                    width: ogImportedAnnotation.Rect.Width,
                    height: ogImportedAnnotation.Rect.Height,
                  };
                }
              }

              const value = getImageExpressionPlaceholderImage(
                bounds.width,
                bounds.height,
                annotation.customData.expressionPlaceholder
              );
              updateCustomStampAnnotationImage(annotation.annotationId, value);
            }
            break;
          default:
            break;
        }
      }
    });
  }, [
    pdfViewerRef.current,
    unselectAllAnnotations,
    originalImportedData,
    getImageExpressionPlaceholderImage,
  ]);

  const previewAnnotations = useCallback(
    async (
      data?: any,
      getDocument?: (value: string) => Promise<any>,
      getDocumentBlobUrl?: (docBlobPart: any) => Promise<string>
    ) => {
      if (!pdfViewerRef.current) {
        return;
      }

      if (!pdfViewerRef.current?.annotationModule) {
        return;
      }

      if (onPreviewAnnotations) {
        const annotationsData = await exportAnnotations();
        onPreviewAnnotations?.(data, annotationsData);
      }

      // Set Preview Data
      if (data) {
        setPreviewData(data);
      } else {
        data = previewData;
      }

      // Unselect
      unselectAllAnnotations();

      pdfViewerRef.current?.annotationCollection.forEach(
        async (annotation: any) => {
          const annotCustomData = annotation.customData as ExpressionData;

          if (
            annotation.customData &&
            expressiveTypes.includes(annotCustomData?.expressionType)
          ) {
            switch (annotation.shapeAnnotationType) {
              case "FreeText":
                {
                  const value =
                    annotCustomData.expressionType ===
                    ExpressionTypes.TextExpression
                      ? get(data, annotCustomData?.expressionName)
                      : annotation.dynamicText;

                  updateAnnotationProperties(annotation.annotationId, {
                    dynamicText: value || " ",
                    fillColor: "transparent",
                    strokeColor: "transparent",
                    thickness: 0,
                  });
                }
                break;
              case "Square":
                {
                  if (
                    annotCustomData.expressionType ===
                    ExpressionTypes.CheckboxExpression
                  ) {
                    const value = get(data, annotCustomData?.expressionName);

                    // If no value, hide from PDF
                    if (value === "True") {
                      updateAnnotationProperties(annotation.annotationId, {
                        fillColor: annotationShapeStyle.current.fillColor,
                        strokeColor: annotationShapeStyle.current.strokeColor,
                        thickness: annotationShapeStyle.current.thickness,
                      });
                    } else {
                      updateAnnotationProperties(annotation.annotationId, {
                        fillColor: "transparent",
                        strokeColor: "transparent",
                        thickness: 0,
                      });
                    }
                  }
                }
                break;
              case "stamp":
                {
                  let value = get(data, annotCustomData?.expressionName);
                  value = await getBaseImageAnnotationData(
                    value,
                    getDocument,
                    getDocumentBlobUrl
                  );

                  updateCustomStampAnnotationImage(
                    annotation.annotationId,
                    value
                  );
                }
                break;
              default:
                break;
            }
          }
        }
      );
    },
    [
      pdfViewerRef.current,
      unselectAllAnnotations,
      previewData,
      getBaseImageAnnotationData,
    ]
  );

  const updateCustomStampAnnotationImage = useCallback(
    (annotationId: string, imageSource: string) => {
      const annoObject = getAnnotationObjectByAnnotationId(annotationId);
      if (!annoObject) {
        return;
      }

      const imageElement = annoObject!.wrapper!.children[0] as ImageElement;
      imageElement["imageSource"] = imageSource;

      updateAnnotationProperties(annotationId, {
        data: imageSource,
      });
    },
    [getAnnotationObjectByAnnotationId]
  );

  const updateAnnotationProperties = useCallback(
    (
      annotationId: string,
      annotationUpdateSettings: PdfAnnotationBaseModel
    ) => {
      const annoObject = getAnnotationObjectByAnnotationId(annotationId);
      if (!annoObject) {
        return;
      }

      pdfViewerRef.current?.nodePropertyChange(
        annoObject,
        annotationUpdateSettings
      );
    },
    [pdfViewerRef.current, getAnnotationObjectByAnnotationId]
  );

  const goToPage = useCallback(
    (pageNumber: number) => {
      const pdfViewerContainer = document.getElementById(pdfViewerId!);
      // Remove all to cleanup
      pdfViewerContainer?.classList.remove(`go-to-page-transition-enter`);
      pdfViewerContainer?.classList.remove(`go-to-page-transition-exit`);
      pdfViewerContainer?.classList.add(`go-to-page-transition-enter`);
      setTimeout(() => {
        pdfViewerRef.current?.navigation.goToPage(pageNumber);
        setTimeout(() => {
          pdfViewerContainer?.classList.add(`go-to-page-transition-exit`);
        }, 300);
      }, 200);
    },
    [pdfViewerId, pdfViewerRef.current]
  );

  const getExpressionAnnotationByExpressionId = useCallback(
    (expressionId: string | undefined): PdfAnnotationBase | undefined => {
      if (!pdfViewerRef.current || !expressionId) {
        return undefined;
      }

      const newAnnotation = pdfViewerRef.current.annotations.find(
        (x) =>
          x.customData && (x.customData as ExpressionData)?.id === expressionId
      );

      return newAnnotation as PdfAnnotationBase;
    },
    [pdfViewerRef.current]
  );

  const exportAnnotations = useCallback(async (): Promise<
    string | undefined
  > => {
    if (!pdfViewerRef.current) {
      return;
    }

    if (pdfViewerRef.current.annotationCollection.length <= 0) {
      // No Annotations
      return;
    }

    // Export
    const exportResultBase64Xfdf =
      await pdfViewerRef.current?.exportAnnotationsAsBase64String(
        AnnotationDataFormat.Xfdf
      );

    // Clean response
    return exportResultBase64Xfdf.replace("data:application/pdf;base64,", "");
  }, [pdfViewerRef.current]);

  const importAnnotations = useCallback(
    async (exportedAnnotationsXFDFBase64: string) => {
      if (!pdfViewerRef.current) {
        return;
      }

      pdfViewerRef.current?.importAnnotation(
        exportedAnnotationsXFDFBase64,
        AnnotationDataFormat.Xfdf
      );
    },
    [pdfViewerRef.current]
  );

  const selectAnnotation = useCallback(
    (annotationId: string) => {
      if (!pdfViewerRef.current) {
        return;
      }

      // Select
      pdfViewerRef.current.annotation.selectAnnotation(annotationId);
      pdfViewerRef.current.toolbar.showAnnotationToolbar(true);
    },
    [pdfViewerRef.current]
  );

  const setup = useCallback((args: { pdfViewerId: string }) => {
    // Setup Properties
    setPdfViewerId(args.pdfViewerId);
  }, []);

  const updateOriginalImportedData = useCallback(
    (originalImportedDataParam?: any) => {
      setOriginalImportedData(originalImportedDataParam);
    },
    []
  );

  return {
    // State
    //pdfViewerRef.current,
    originalImportedData,
    pdfViewerId,
    previewData,

    // Methods
    handleAnnotationSelect,
    updateExpressionSelectedClassName,
    removeAllExpressionSelectedClassName,
    getAnnotationByAnnotationId,
    getAnnotationObjectByAnnotationId,
    handleAnnotationUnselect,
    unselectAnnotation,
    unselectAllAnnotations,
    annotationRemove,
    addExpressionAnnotation,
    getDefaultExpressionAnnotationDimensions,
    addFreeTextAnnotation,
    addRectangleAnnotation,
    addCircleAnnotation,
    addCheckboxExpressionAnnotation,
    addTextExpressionAnnotation,
    addImageExpressionAnnotation,
    getImageExpressionPlaceholderImage,
    designAnnotations,
    previewAnnotations,
    updateCustomStampAnnotationImage,
    updateAnnotationProperties,
    goToPage,
    getExpressionAnnotationByExpressionId,
    exportAnnotations,
    importAnnotations,
    selectAnnotation,
    setup,
    updateOriginalImportedData,
  };
};
